import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { theme } from './styles/theme';
import './styles/animations.css';
import { AuthProvider, ErrorProvider } from './contexts';
import { ProtectedRoute, RoleGuard, ErrorBoundary } from './components';

// Lazy load page components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const TutorialPage = lazy(() => import('./pages/TutorialPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const DomainManagementPage = lazy(() => import('./pages/DomainManagementPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const VideoAdsPage = lazy(() => import('./pages/VideoAdsPage'));
const SystemSettingsPage = lazy(() =>
  import('./pages/SystemSettingsPage').then((module) => ({ default: module.SystemSettingsPage }))
);
const StaffUsersPage = lazy(() => import('./pages/StaffUsersPage'));

// Loading fallback component
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <ErrorProvider>
          <AuthProvider>
            <Router>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/tutorial"
                    element={<ProtectedRoute><TutorialPage /></ProtectedRoute>}
                  />
                  <Route
                    path="/dashboard/faq"
                    element={<ProtectedRoute><FAQPage /></ProtectedRoute>}
                  />
                  <Route
                    path="/dashboard/users"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['super_admin']} redirectUnauthorized>
                          <UserManagementPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/domains"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['marketing_agent', 'staff']} redirectUnauthorized>
                          <DomainManagementPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/domains/all"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['super_admin']} redirectUnauthorized>
                          <DomainManagementPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/analytics"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['marketing_agent']} redirectUnauthorized>
                          <AnalyticsPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/video-ads"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['marketing_agent', 'staff', 'super_admin']}>
                          <VideoAdsPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/staff-users"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['marketing_agent']} redirectUnauthorized>
                          <StaffUsersPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/analytics/system"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['super_admin']} redirectUnauthorized>
                          <AnalyticsPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/system-settings"
                    element={
                      <ProtectedRoute>
                        <RoleGuard allowedRoles={['super_admin']} redirectUnauthorized>
                          <SystemSettingsPage />
                        </RoleGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/dashboard/contact" element={<ProtectedRoute><ContactUsPage /></ProtectedRoute>} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Suspense>
            </Router>
          </AuthProvider>
        </ErrorProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
